import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { rd } from '@passionware/monads';

import { Button } from 'v1/components/shared';
import { cn } from 'v5/platform/dom/cn';

import classnames from 'classnames';
import './ButtonWithDropdown.scss';
import {
  DropdownMenu,
  DropdownMenuButtonArrow,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import {
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxQueryRenderer,
  InlineCombobox
} from 'v5/design-system/Combobox';
import { focusRings } from '../../../../../v5/design-system/_common/focusRing.js';

/**
 * Existing usages:
 * <ButtonWithDropdown
 *           className="btn btn-primary"
 *           loading={contacts.loading === 'CREATE_CONTACT'}
 *           actions={[
 *             {
 *               text: 'Save',
 *               action: submitForm
 *             },
 *             {
 *               text: 'Save and go to profile',
 *               icon: '/images/icon_arrow_top_right.svg',
 *               action: submitFormAndGoToProfile
 *             }
 *           ]}
 *         />
 *
 * Enhanced with nested menus (recursive):
 * <ButtonWithDropdown
 *           className="btn btn-primary"
 *           loading={contacts.loading === 'CREATE_CONTACT'}
 *           actions={[
 *             {
 *               text: 'Save',
 *               action: submitForm
 *             },
 *             {
 *               text: 'More Options',
 *               icon: '/images/icon_arrow_right.svg',
 *               submenu: [
 *                 {
 *                   text: 'Sub Option 1',
 *                   action: subAction1
 *                 },
 *                 {
 *                   text: 'Sub Option 2',
 *                   action: subAction2
 *                 },
 *                 {
 *                   text: 'Advanced',
 *                   icon: '/images/icon_arrow_right.svg',
 *                   submenu: [
 *                     {
 *                       text: 'Advanced Option 1',
 *                       action: advancedAction1
 *                     },
 *                     {
 *                       text: 'Advanced Option 2',
 *                       action: advancedAction2
 *                     }
 *                   ]
 *                 }
 *               ]
 *             }
 *           ]}
 *         />
 *
 * Enhanced with search functionality:
 * <ButtonWithDropdown
 *           className="btn btn-primary"
 *           loading={contacts.loading === 'CREATE_CONTACT'}
 *           actions={[
 *             {
 *               text: 'Save',
 *               action: submitForm
 *             },
 *             {
 *               text: 'Add to shortlist',
 *               icon: '/images/icon_sidebar_shortlists_colour.svg',
 *               search: {
 *                 searchFunction: (query) => [
 *                   { text: 'Shortlist 1', action: () => addToShortlist(1) },
 *                   { text: 'Shortlist 2', action: () => addToShortlist(2) },
 *                   { text: 'My Favorites', action: () => addToShortlist(3) }
 *                 ].filter(option =>
 *                   option.text.toLowerCase().includes(query.toLowerCase())
 *                 )
 *               }
 *             }
 *           ]}
 *         />
 *
 * Note: The searchFunction should return an array of options with text and action properties:
 * const searchFunction = (query: string) => [
 *   { text: string, action: () => void, icon?: string | ReactElement }
 * ];
 */
export default class ButtonWithDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      dropdownOpen: false
    };
  }

  handleDropdownOpenChange = open => {
    this.setState({ dropdownOpen: open });
  };

  handleComboboxItemSelect = action => {
    // Execute the action
    action();
    // Close the dropdown
    this.setState({ dropdownOpen: false });
  };

  renderMenuItem = (action, index) => {
    const renderIcon = icon => {
      if (!icon) return null;
      if (React.isValidElement(icon)) {
        return icon;
      }
      return <img src={icon} alt="" />;
    };

    if (action.search) {
      // Render search submenu with combobox
      const { searchFunction } = action.search;
      return (
        <DropdownMenuSub key={index}>
          <DropdownMenuSubTrigger size="lg">
            {renderIcon(action.icon)}
            {action.text}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-0">
            <InlineCombobox size="lg">
              <ComboboxInput />
              <ComboboxList>
                <ComboboxQueryRenderer
                  emptyContent={<div>No options found</div>}
                >
                  {(query, isOpen) => {
                    const options = searchFunction(query);
                    return rd.of(
                      <ComboboxGroup>
                        {options.map((option, optionIndex) => (
                          <ComboboxItem
                            key={optionIndex}
                            size="lg"
                            onSelect={() =>
                              this.handleComboboxItemSelect(option.action)
                            }
                          >
                            {renderIcon(option.icon)}
                            {option.text}
                          </ComboboxItem>
                        ))}
                      </ComboboxGroup>
                    );
                  }}
                </ComboboxQueryRenderer>
              </ComboboxList>
            </InlineCombobox>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    if (action.submenu) {
      // Render nested submenu recursively
      return (
        <DropdownMenuSub key={index}>
          <DropdownMenuSubTrigger size="lg">
            {renderIcon(action.icon)}
            {action.text}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {action.submenu.map((subAction, subIndex) =>
              this.renderMenuItem(subAction, subIndex)
            )}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      );
    }

    // Render regular menu item
    return (
      <DropdownMenuItem
        size="lg"
        key={index}
        onSelect={() => this.handleComboboxItemSelect(action.action)}
      >
        {renderIcon(action.icon)}
        {action.text}
      </DropdownMenuItem>
    );
  };

  renderDropdown = () => {
    const { actions: _actions = [] } = this.props;
    const actions = _actions.slice(1);
    return (
      <DropdownMenu
        open={this.state.dropdownOpen}
        onOpenChange={this.handleDropdownOpenChange}
      >
        <DropdownMenuTrigger
          className={cn(
            'bg-transparent shrink-0 flex items-center px-2.5 justify-center *:size-4 border-l border-l-current/20',
            'rounded-r-sm',
            focusRings.focus.visible
          )}
        >
          <DropdownMenuButtonArrow />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          {actions.map((action, index) => this.renderMenuItem(action, index))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };
  renderPrimaryButton = () => {
    const { loading, actions, disabled } = this.props;
    const primaryButton = actions[0];

    return (
      <Button
        onClick={primaryButton.action}
        className={
          'ButtonWithDropdown-primaryButton ' +
          focusRings.focus.visible +
          ' rounded-l-sm'
        }
        loading={loading}
        disabled={disabled}
        stopPropagation
      >
        {primaryButton.text}
      </Button>
    );
  };
  render() {
    const { actions, disabled, loading } = this.props;
    return (
      <div
        className={classnames([
          'ButtonWithDropdown',
          'tailwind-root',
          this.props.className,
          { disabled: disabled || loading }
        ])}
      >
        {this.renderPrimaryButton()}
        {actions.length > 1 && this.renderDropdown()}
      </div>
    );
  }
}

ButtonWithDropdown.propTypes = {
  className: PropTypes.string, // Uses button classes - 'btn btn-default' etc
  actions: PropTypes.array.isRequired,
  /* TODO: actions[0] is required, and is a primary button with shape:
			{
				action
				text
			}
		*/
  // TODO: rest is array of 'links' as passed to DropdownButton
  // Enhanced to support nested menus (recursive):
  // - Regular action: { text, action, icon? }
  // - Nested action: { text, icon?, submenu: [{ text, action, icon? }] }
  // - Submenu items can also have submenus for unlimited nesting levels
  // - Search action: { text, icon?, search: { searchFunction: (query) => Array<{ text, action, icon? }> } }
  // TODO: the following (loading, disabled) are passed to Button, so duplicate fttb
  loading: PropTypes.any, // TODO: type - passed to button but not used in Button?
  disabled: PropTypes.bool
};
